import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import { TQTabView } from 'components/TQDataGrid/utils/grid-types';
import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import { GetThreadsResponse } from 'types/threads';
import { BulkAssignAccountsRequest, BulkAssignAccountsResponse, CloneTQModelRequest, TQModel } from 'types/tqModels';
import { TQModelAccountsCount, TQModelTreeNode } from 'types/tqModelTree';
import { GetExportFileResponse } from 'types/worksheets';
import type { APIError } from 'utils/errors';
import { UNASSIGNED_ID } from 'utils/tree';

const apiUrl = '/api/v1/tq/hierarchy-models/';

export type TQModelRequest = Pick<TQModel, 'name' | 'planning_cycle_id' | 'model_type'>;

const getTQModel = (tqModelId: StringifiedUUID) => getRequest<null, TQModel>(`${apiUrl}${tqModelId}/`);

const useGetTQModelQuery = (tqModelId: StringifiedUUID) => {
  const QUERY_KEY = ['tqModel', tqModelId];
  const queryClient = useQueryClient();

  const query = useQuery<TQModel, APIError>(QUERY_KEY, () => getTQModel(tqModelId));
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

type TQModelTreeParams = {
  territoryViewId?: StringifiedUUID | null;
};

const getTQModelTree = (tqModelId: StringifiedUUID, params: TQModelTreeParams = {}) => {
  const queryParams = new URLSearchParams();
  if (params.territoryViewId) {
    queryParams.set('territory_view_id', params.territoryViewId);
  }
  const queryString = queryParams.toString();
  return getRequest<null, APIList<TQModelTreeNode>>(
    `${apiUrl}${tqModelId}/tree/${queryString ? `?${queryString}` : ''}`,
  );
};

const useGetTQModelTreeQuery = (tqModelId: StringifiedUUID, params: TQModelTreeParams = {}) => {
  const QUERY_KEY = ['tqModelTree', tqModelId, params.territoryViewId ?? 'main'];
  const queryClient = useQueryClient();

  const query = useQuery<APIList<TQModelTreeNode>, APIError>(QUERY_KEY, () => getTQModelTree(tqModelId, params));
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

type AccountsCountsOptions = {
  includeDraft?: boolean;
  territoryViewId?: StringifiedUUID | null;
};

const getTQModelAccountsCounts = (tqModelId: StringifiedUUID, options: AccountsCountsOptions = {}) =>
  getRequest<null, APIList<TQModelAccountsCount>>(
    `${apiUrl}${tqModelId}/accounts-counts/?include_draft=${options.includeDraft ? 'true' : 'false'}${
      options.territoryViewId ? `&territory_view_id=${options.territoryViewId}` : ''
    }`,
  );

export const getAccountsCountsQueryKey = (
  tqModelId: StringifiedUUID,
  includeDraft: boolean,
  territoryViewId?: StringifiedUUID | null,
) => [
  'tqModelTree',
  tqModelId,
  'accounts-counts',
  includeDraft ? 'with-draft' : 'published',
  'territory-view-id',
  territoryViewId ?? 'main',
];

const useGetTQModelAccountsCountsQuery = (tqModelId: StringifiedUUID, options: AccountsCountsOptions = {}) => {
  const includeDraft = options.includeDraft ?? false;
  const territoryViewId = options?.territoryViewId ?? undefined;
  const QUERY_KEY = getAccountsCountsQueryKey(tqModelId, includeDraft, territoryViewId);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<TQModelAccountsCount>, APIError>(
    QUERY_KEY,
    () => getTQModelAccountsCounts(tqModelId, { includeDraft, territoryViewId }),
    {
      keepPreviousData: includeDraft,
      onSettled: (data) => {
        if (!includeDraft) {
          // We pre-fill the draft counts query with the published counts, so that when user switches
          // to "include draft" view, we already have some data to show while the query is loading
          // (it's safe to show them temporarily, as it fixes flickering)
          const DRAFT_QUERY_KEY = getAccountsCountsQueryKey(tqModelId, true, territoryViewId);
          queryClient.setQueryData(
            DRAFT_QUERY_KEY,
            (current: APIList<TQModelAccountsCount> | undefined) => (current ?? data) as APIList<TQModelAccountsCount>,
          );
        }
      },
    },
  );
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

const createTQModel = (csrfToken: string) => (tqModel: TQModelRequest) => {
  return postRequest<TQModelRequest, TQModel>(apiUrl, tqModel, csrfToken);
};

const useCreateTQModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<TQModel, APIError, TQModelRequest> = {},
) => {
  return {
    mutation: useMutation<TQModel, APIError, TQModelRequest>(createTQModel(csrfToken), options),
  };
};

const bulkAssignAccounts =
  (tqModelId: StringifiedUUID, csrfToken: string, territoryViewId?: StringifiedUUID | null) =>
  (req: BulkAssignAccountsRequest) => {
    const queryParams = new URLSearchParams();
    if (territoryViewId) {
      queryParams.set('territory_view_id', territoryViewId);
    }
    const queryString = queryParams.toString();
    return postRequest<BulkAssignAccountsRequest, BulkAssignAccountsResponse>(
      `${apiUrl}${tqModelId}/bulk-assign/${queryString ? `?${queryString}` : ''}`,
      req,
      csrfToken,
    );
  };

const useBulkAssignAccountsMutation = (
  csrfToken: string,
  tqModelId: StringifiedUUID,
  territoryViewId?: StringifiedUUID | null,
  options: UseMutationOptions<BulkAssignAccountsResponse, APIError, BulkAssignAccountsRequest> = {},
) => {
  return {
    mutation: useMutation<BulkAssignAccountsResponse, APIError, BulkAssignAccountsRequest>(
      bulkAssignAccounts(tqModelId, csrfToken, territoryViewId),
      options,
    ),
  };
};

const cloneTQModel = (tqModelId: StringifiedUUID, csrfToken: string) => (tqModel: CloneTQModelRequest) => {
  return postRequest<CloneTQModelRequest, TQModel>(`${apiUrl}${tqModelId}/clone/`, tqModel, csrfToken);
};

const useCloneTQModelMutation = (
  csrfToken: string,
  tqModelId: StringifiedUUID,
  options: UseMutationOptions<TQModel, APIError, CloneTQModelRequest> = {},
) => {
  return {
    mutation: useMutation<TQModel, APIError, CloneTQModelRequest>(cloneTQModel(tqModelId, csrfToken), { ...options }),
  };
};

// can we consider not using strict serializer on backend here to save having to split up the payload
type UpdateTQModelProps = {
  tqModelId: StringifiedUUID;
  tqModel: TQModelRequest;
};

const updateTQModel =
  (csrfToken: string) =>
  ({ tqModelId, tqModel }: UpdateTQModelProps) => {
    return putRequest<TQModelRequest, TQModel>(`${apiUrl}${tqModelId}/`, tqModel, csrfToken);
  };

const useUpdateTQModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<TQModel, APIError, UpdateTQModelProps> = {},
) => {
  return {
    mutation: useMutation<TQModel, APIError, UpdateTQModelProps>(updateTQModel(csrfToken), options),
  };
};

const deleteTQModel = (csrfToken: string) => (tqModelId: StringifiedUUID) => {
  return deleteRequest<null, TQModel>(`${apiUrl}${tqModelId}/`, null, csrfToken);
};

const useDeleteTQModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<TQModel, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<TQModel, APIError, StringifiedUUID>(deleteTQModel(csrfToken), options),
  };
};

const getExportFileQuery = (
  tqModelId: StringifiedUUID,
  tabView: TQTabView,
  territoryId?: string,
  territoryViewId?: string,
) => {
  const queryParams = new URLSearchParams();
  queryParams.set('data_object_schema_type', tabView);
  if (territoryId) {
    queryParams.set('territory_id', territoryId);
  }
  if (territoryViewId) {
    queryParams.set('territory_view_id', territoryViewId);
  }
  return getRequest<null, GetExportFileResponse>(`${apiUrl}${tqModelId}/csv_export?${queryParams.toString()}`);
};

const territoriesImportQuery = (
  tqModelId: StringifiedUUID,
  csrfToken: string,
  formData: FormData,
  territoryId?: string,
  territoryViewId?: string,
) => {
  const queryParams = new URLSearchParams();
  if (territoryId) {
    queryParams.set('territory_id', territoryId);
  }
  if (territoryViewId) {
    queryParams.set('territory_view_id', territoryViewId);
  }
  const queryString = queryParams.toString();

  return postRequest<{}, null>(
    `${apiUrl}${tqModelId}/csv-import/${queryString ? `?${queryString}` : ''}`,
    {},
    csrfToken,
    undefined,
    {
      body: formData,
      headers: {
        Accept: 'application/json',
        'X-CSRFToken': csrfToken,
      },
    },
  );
};

const getThreads = (tqModelId: StringifiedUUID, territoryId: StringifiedUUID) =>
  getRequest<null, GetThreadsResponse>(
    `${apiUrl}${tqModelId}/threads/${territoryId ? `?territory_id=${territoryId}` : ''}`,
  );

const useGetThreads = (
  territoryId: StringifiedUUID | undefined,
  tqModelId: StringifiedUUID,
  options: UseQueryOptions<GetThreadsResponse, APIError> = {},
) => {
  const queryClient = useQueryClient();
  const QUERY_KEY = ['threads', territoryId];
  const query = useQuery<GetThreadsResponse, APIError>(QUERY_KEY, () => getThreads(tqModelId, territoryId as string), {
    ...options,
    enabled: !!territoryId && territoryId !== UNASSIGNED_ID,
  });

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return {
    query,
    invalidateQuery,
  };
};
export const TQModelsService = {
  apiUrl,
  getAccountsCountsQueryKey,
  useGetTQModelQuery,
  useGetTQModelTreeQuery,
  useGetTQModelAccountsCountsQuery,
  useCreateTQModelMutation,
  useCloneTQModelMutation,
  useUpdateTQModelMutation,
  useDeleteTQModelMutation,
  getTQModelTree,
  getTQModelAccountsCounts,
  getExportFileQuery,
  territoriesImportQuery,
  useGetThreads,
  useBulkAssignAccountsMutation,
};
